/** Боковая карточка поставки (`card=supply:<кабинет>:<supply_id>`, ручка `/supply/{id}`): шапка и
 *  путь крупно, профиль покупателей (доли по регионам), места сейчас, товары по 8 корзинам
 *  регионов. Товар → карточка оффера по `offer_key` товара (контракт v1.3); null — «оффер не
 *  определён» без ссылки (ключ на фронте не угадываем). Плашки свежести своего ответа —
 *  `BuildBanners`. */
import React from "react";

import type { WbRegionsState, WbSupplyCard, WbSupplyItem } from "@/api/wbRegions";
import { mpProductUrl } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import { fmtWhen, intSigned, pct } from "../ozon-analytics/format";
import { tdNum, thClass } from "../ozon-analytics/ui";
import { BuildBanners } from "./BuildBanners";
import { useBuildSync } from "./buildSync";
import { ErrorBox, Loading, Missing, Qty } from "./cells";
import { dec1 } from "./format";
import { useWbSupply } from "./queries";
import { SidePanel } from "./SidePanel";
import type { CardRef, SupplyCardRef } from "./state";
import { FlagsList, FlowCell, MethodBadge, PathChips, PlannedMark, ReconCell } from "./SupplyBits";
import { placeName, PROFILE_MARGIN_MIN, profileMarginTitle, storageLabel } from "./supplyFormat";

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ProfileBlock({ data }: { data: WbSupplyCard }): React.ReactElement {
  const p = data.profile;
  // доли профиля — по корзинам, у которых в ответе есть `share_<код>` (у «не определён» доли нет)
  const buckets = data.region_buckets.filter((b) => `share_${b.code}` in p);
  return (
    <section className="space-y-1" data-testid="supply-profile">
      <h3 className="text-sm font-medium">Профиль покупателей (первые 14 дней)</h3>
      <div className="text-xs text-muted-foreground">
        заказов в профиле: {p.profile_n === null ? <Missing /> : p.profile_n} · медиана доставки:{" "}
        {p.median_delivery_days === null ? <Missing /> : `${dec1(p.median_delivery_days)} дн.`} · регион профиля:{" "}
        {p.profile_region_wb ?? <Missing />} ·{" "}
        <span className="cursor-help" title={profileMarginTitle(p.profile_margin)} data-testid="profile-margin">
          отрыв профиля:{" "}
          {p.profile_margin === null ? (
            <Missing text="профиля нет" />
          ) : (
            <>
              {p.profile_margin.toLocaleString("ru-RU", { maximumFractionDigits: 3 })}
              {p.profile_margin < PROFILE_MARGIN_MIN && <span className="text-muted-foreground"> (только справочно)</span>}
            </>
          )}
        </span>
      </div>
      <div className="flex flex-wrap gap-1 text-[11px]">
        {buckets.map((b) => {
          const v = p[`share_${b.code}`] ?? null;
          return (
            <span key={b.code} className="rounded border px-1.5 py-0.5" data-testid="profile-share">
              {b.label}: {v === null ? <Missing /> : pct(v)}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function PlacesTable({ data }: { data: WbSupplyCard }): React.ReactElement {
  const places = data.supply.places;
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium">
        Места сейчас{" "}
        <span className="text-xs font-normal text-muted-foreground">
          (день хранения {fmtWhen(data.supply.storage_date_now)}; оценка)
        </span>
      </h3>
      {places.length === 0 ? (
        <div className="text-xs text-muted-foreground">Штук поставки в хранении сейчас нет.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs" data-testid="supply-places">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>Склад, регион</th>
                <th className={thClass}>Шт.</th>
                <th className={thClass}>Уверенность</th>
                <th className={cn(thClass, "text-left")}>Способы</th>
                <th className={thClass}>Заморожено</th>
              </tr>
            </thead>
            <tbody>
              {places.map((p, i) => (
                <tr key={i} className="border-t" data-testid="supply-place">
                  <td className="px-2 py-1">{placeName(p)}</td>
                  <td className={tdNum}>
                    <Qty v={p.qty} />
                  </td>
                  <td className={tdNum}>{p.confidence === null ? <Missing text="—" /> : pct(p.confidence)}</td>
                  <td className="px-2 py-1 text-muted-foreground">{p.methods ?? "—"}</td>
                  <td className={tdNum}>{p.frozen === null ? <Missing /> : p.frozen ? "да" : "нет"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OfferLinks({
  item,
  onOpenCard,
}: {
  item: WbSupplyItem;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const key = item.offer_key;
  if (key === null) {
    return (
      <span className="text-[10px] text-muted-foreground" data-testid="no-offer" title="offer_key товара пуст: nm или размер не определены">
        оффер не определён
      </span>
    );
  }
  return (
    <button
      type="button"
      className="text-[10px] text-primary hover:underline"
      data-testid="item-offer-link"
      onClick={() => onOpenCard({ kind: "offer", legal: item.legal_name, offerKey: key })}
    >
      карточка оффера
    </button>
  );
}

function ItemsTable({
  data,
  onOpenCard,
}: {
  data: WbSupplyCard;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const buckets = data.region_buckets;
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium">Товары поставки</h3>
      {data.items.length === 0 ? (
        <div className="text-xs text-muted-foreground">Товаров поставки в витрине нет.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs" data-testid="supply-items">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>Товар</th>
                <th className={thClass} title="заявлено — в подсказке">Принято</th>
                <th className={thClass}>В хранении</th>
                {buckets.map((b) => (
                  <th key={b.code} className={thClass} title={b.label}>
                    {b.label}
                  </th>
                ))}
                <th className={thClass}>Продано</th>
                <th className={thClass}>Возвр.</th>
                <th className={thClass}>Вывоз</th>
                <th className={thClass}>Расхожд.</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const url = mpProductUrl("wb", it.mp_sku);
                return (
                  <tr key={`${it.mp_sku}:${it.mp_size}`} className="border-t align-top" data-testid="supply-item">
                    <td className="px-2 py-1">
                      <div className="font-medium">{it.vendor_code ?? "без артикула"}</div>
                      <div className="text-muted-foreground">
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer" className="hover:underline">
                            nm {it.mp_sku}
                          </a>
                        ) : (
                          "nm нет"
                        )}
                        {it.mp_size && it.mp_size !== "0" && <> · размер {it.mp_size}</>}
                      </div>
                      {it.name_1c && <div className="max-w-[220px] truncate text-muted-foreground">{it.name_1c}</div>}
                      <OfferLinks item={it} onOpenCard={onOpenCard} />
                    </td>
                    <td className={tdNum} title={`заявлено: ${it.qty_planned ?? "нет данных"}; в продаже: ${it.qty_ready_for_sale ?? "нет данных"}`}>
                      <Qty v={it.qty_accepted} />
                    </td>
                    <td className={tdNum}>
                      <Qty v={it.in_storage_now} />
                    </td>
                    {buckets.map((b) => (
                      <td key={b.code} className={tdNum} data-testid={`item-bucket-${b.code}`}>
                        <Qty v={it[`in_storage_now_${b.code}`] ?? null} />
                      </td>
                    ))}
                    <td className={tdNum}>
                      <Qty v={it.sold_fbo} />
                    </td>
                    <td className={tdNum}>
                      <Qty v={it.returns} />
                    </td>
                    <td className={tdNum}>
                      <Qty v={it.to_seller} />
                    </td>
                    <td className={tdNum} data-testid="item-recon">
                      {it.recon_diff === null ? <Missing /> : intSigned(it.recon_diff)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SupplyCardBody({
  card,
  pageState,
  onOpenCard,
}: {
  card: SupplyCardRef;
  pageState: WbRegionsState;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const q = useWbSupply(card.legal, card.supplyId);
  const mixed = useBuildSync([pageState, q.data?.state]);
  if (q.isLoading) return <Loading text="Загружаю карточку поставки…" />;
  if (q.isError) return <ErrorBox error={q.error} what="карточку поставки (/supply)" />;
  const data = q.data!;
  const s = data.supply;
  return (
    <div className="space-y-4" data-testid="supply-card-body">
      <BuildBanners states={[data.state]} mixed={mixed} />
      <section className="space-y-3">
        <h2 className="text-base font-semibold">
          Поставка {s.supply_id} <span className="text-sm font-normal text-muted-foreground">· {s.legal_name}</span>
        </h2>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <Field label="Дата поставки">{s.supply_date ? fmtWhen(s.supply_date) : <Missing text="без даты" />}</Field>
          <Field label="Принята">{s.accepted_date ? fmtWhen(s.accepted_date) : <Missing />}</Field>
          <Field label="Статус WB">
            {s.status_name ?? <Missing />} <PlannedMark s={s} />
          </Field>
          <Field label="Хранение">{storageLabel(s.storage_status)}</Field>
          <Field label="Метод · уверенность">
            <MethodBadge s={s} profileMargin={data.profile.profile_margin} />
          </Field>
          <Field label="Принято · продано · возвр. · вывоз">
            <FlowCell s={s} />
          </Field>
          <Field label="Расхождение">
            <ReconCell s={s} />
          </Field>
          <Field label="Флаги">
            <FlagsList s={s} />
          </Field>
        </dl>
        <PathChips s={s} large />
        {s.confidence_reason && (
          <div className="text-xs text-muted-foreground">Уверенность оценки: {s.confidence_reason}</div>
        )}
        <div className="text-xs text-muted-foreground">
          В хранении сейчас: <Qty v={s.in_storage_now_total} /> шт. · первый / последний день в хранении:{" "}
          {fmtWhen(s.storage_first_date)} / {fmtWhen(s.storage_last_date)} · на замороженных / исчезнувших складах в
          последний день: <Qty v={s.frozen_last_qty} />
        </div>
      </section>
      <ProfileBlock data={data} />
      <PlacesTable data={data} />
      <ItemsTable data={data} onOpenCard={onOpenCard} />
    </div>
  );
}

export function SupplyCard({
  card,
  pageState,
  onClose,
  onOpenCard,
}: {
  card: SupplyCardRef;
  /** свежесть ответа страницы (/meta) — сборка карточки сверяется с ней */
  pageState: WbRegionsState;
  onClose: () => void;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  return (
    <SidePanel title="Карточка поставки WB" testId="supply-card" resetKey={`${card.legal}:${card.supplyId}`} onClose={onClose}>
      <SupplyCardBody card={card} pageState={pageState} onOpenCard={onOpenCard} />
    </SidePanel>
  );
}
